import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./CatalogItemMeta.module.css";
import {
  formatTypeLabel,
  getAboutSections,
  getListeningServices,
  getMetaSubtitle,
  normalizeTags,
} from "../../../Hooks/catalogPresentation";
import {
  lockBodyScroll,
  unlockBodyScroll,
} from "../../../Hooks/bodyScrollLock";
import { useCatalogBrowseFilter } from "../../MusicCatalog/CatalogBrowseFilterContext";

/**
 * Release chrome for MusicPlayer.
 * - zone="header": title row only (above embed)
 * - zone="footer": About (left) · tags (right); About opens a metadata modal
 * - zone="both": header + footer
 */
const CatalogItemMeta = ({
  item,
  title,
  compact = false,
  maxTags = 2,
  showAbout = true,
  showNowPlayingHint = false,
  zone = "both",
  aboutOpen: aboutOpenControlled,
  onAboutOpenChange,
}) => {
  const [aboutOpenInternal, setAboutOpenInternal] = useState(false);
  const titleId = useId();
  const { isTagSelected, toggleTag, applyTagAndScroll } =
    useCatalogBrowseFilter();
  const isAboutControlled = typeof onAboutOpenChange === "function";
  const aboutOpen = isAboutControlled
    ? !!aboutOpenControlled
    : aboutOpenInternal;
  const setAboutOpen = (next) => {
    if (isAboutControlled) onAboutOpenChange(next);
    else setAboutOpenInternal(next);
  };

  const handleTagClick = (tag) => {
    if (isTagSelected(tag)) {
      toggleTag(tag);
    } else {
      applyTagAndScroll(tag);
    }
    setAboutOpen(false);
  };

  useEffect(() => {
    if (!aboutOpen) return undefined;
    lockBodyScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setAboutOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      unlockBodyScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [aboutOpen]);

  if (!item && !title) return null;

  const displayTitle = (item && item.title) || title || "";
  const typeLabel = item && item.type ? formatTypeLabel(item.type) : "";
  const tagsPreview = item ? normalizeTags(item.tags, { max: maxTags }) : [];
  const allTags = item ? normalizeTags(item.tags) : [];
  const aboutSections = item ? getAboutSections(item) : [];
  const releaseLabel = item ? getMetaSubtitle(item) : "";
  const services = item ? getListeningServices(item) : [];
  const isFeatured =
    item &&
    (item.isFeaturedPlaylist === true ||
      String(item.isFeaturedPlaylist || "")
        .toLowerCase()
        .trim() === "true");
  const isDefault =
    item &&
    (item.isDefaultPlaylist === true ||
      String(item.isDefaultPlaylist || "")
        .toLowerCase()
        .trim() === "true");

  const showHeader = zone === "header" || zone === "both";
  const showFooter = zone === "footer" || zone === "both";
  // Footer: About always when allowed; tags when present
  const hasFooterContent = !!(showAbout || tagsPreview.length > 0);

  const header = showHeader && (
    <div
      className={
        styles["meta-header"] +
        " " +
        styles[compact ? "compact" : "full"] +
        (showNowPlayingHint ? " " + styles["with-now-playing"] : "")
      }
    >
      {showNowPlayingHint && (
        <p className={styles["now-playing-label"]}>Now playing</p>
      )}
      <div className={styles["title-row"]}>
        {typeLabel && (
          <span className={styles["type-badge"]}>{typeLabel}</span>
        )}
        {displayTitle && (
          <h3 className={styles["item-title"]}>{displayTitle}</h3>
        )}
      </div>
    </div>
  );

  const hasScrollContent =
    aboutSections.length > 0 ||
    allTags.length > 0 ||
    services.length > 0;

  // Modal body order: About → Vibe → Listen also on → other prose → Notes last
  const isNotesSection = (section) =>
    section.key === "notes" ||
    String(section.label || "").toLowerCase() === "notes";
  const isAboutSection = (section) =>
    section.key === "description" ||
    section.key === "about" ||
    String(section.label || "").toLowerCase() === "about";
  const aboutMainSections = aboutSections.filter(isAboutSection);
  const notesSections = aboutSections.filter(isNotesSection);
  const otherProseSections = aboutSections.filter(
    (section) => !isAboutSection(section) && !isNotesSection(section),
  );

  const renderProseSection = (section) => (
    <section
      key={section.key}
      className={
        styles["modal-section"] + " " + styles["modal-section-prose"]
      }
    >
      <h3 className={styles["modal-section-title"]}>{section.label}</h3>
      <p className={styles["modal-prose"]}>{section.text}</p>
    </section>
  );

  const vibeSection =
    allTags.length > 0 ? (
      <section className={styles["modal-section"]}>
        <div className={styles["modal-section-heading"]}>
          <h3 className={styles["modal-section-title"]}>Vibe</h3>
          <p className={styles["modal-section-hint"]}>
            Tap a tag to filter the catalog
          </p>
        </div>
        <ul className={styles["modal-tag-list"]}>
          {allTags.map((tag) => {
            const active = isTagSelected(tag);
            return (
              <li key={tag}>
                <button
                  type="button"
                  className={
                    styles["tag-chip"] +
                    " " +
                    styles["tag-chip-button"] +
                    " " +
                    styles["modal-tag-chip"] +
                    (active ? " " + styles["tag-chip-active"] : "")
                  }
                  aria-pressed={active}
                  onClick={() => handleTagClick(tag)}
                >
                  {tag}
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    ) : null;

  const modal =
    aboutOpen &&
    item &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        className={styles["modal-backdrop"]}
        role="presentation"
        onClick={() => setAboutOpen(false)}
      >
        <div
          className={styles["modal-panel"]}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
        >
          <header className={styles["modal-header"]}>
            <button
              type="button"
              className={styles["modal-close"]}
              onClick={() => setAboutOpen(false)}
              aria-label="Close about this release"
            >
              ×
            </button>
            <p className={styles["modal-kicker"]}>About this release</p>
            <h2 id={titleId} className={styles["modal-title"]}>
              {displayTitle || "Untitled"}
            </h2>

            {(typeLabel ||
              releaseLabel ||
              isFeatured ||
              isDefault) && (
              <dl className={styles["modal-facts"]}>
                {typeLabel && (
                  <div className={styles["modal-fact"]}>
                    <dt>Type</dt>
                    <dd>
                      <span className={styles["type-badge"]}>{typeLabel}</span>
                    </dd>
                  </div>
                )}
                {releaseLabel && (
                  <div className={styles["modal-fact"]}>
                    <dt>Released</dt>
                    <dd className={styles["modal-fact-value"]}>
                      {releaseLabel}
                    </dd>
                  </div>
                )}
                {(isFeatured || isDefault) && (
                  <div className={styles["modal-fact"]}>
                    <dt>Highlights</dt>
                    <dd className={styles["modal-fact-chips"]}>
                      {isFeatured && (
                        <span className={styles["modal-chip-accent"]}>
                          Featured
                        </span>
                      )}
                      {isDefault && (
                        <span className={styles["modal-chip"]}>
                          Default playlist
                        </span>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </header>

          <div
            className={styles["modal-scroll"]}
            data-scroll-lock-scrollable
          >
            {aboutMainSections.map(renderProseSection)}

            {vibeSection}

            {services.length > 0 && (
              <section className={styles["modal-section"]}>
                <div className={styles["modal-section-heading"]}>
                  <h3 className={styles["modal-section-title"]}>
                    Listen also on
                  </h3>
                  <p className={styles["modal-section-hint"]}>
                    Opens the native page in a new tab
                  </p>
                </div>
                <ul className={styles["modal-service-list"]}>
                  {services.map((service) => (
                    <li key={service.slug}>
                      <a
                        className={styles["modal-service-chip"]}
                        href={service.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className={styles["modal-service-label"]}>
                          {service.label}
                        </span>
                        <span
                          className={styles["modal-service-external"]}
                          aria-hidden="true"
                        >
                          ↗
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {otherProseSections.map(renderProseSection)}

            {notesSections.map(renderProseSection)}

            {!hasScrollContent && (
              <p className={styles["modal-empty"]}>
                More details for this release will appear here when they are
                added.
              </p>
            )}
          </div>

          <footer className={styles["modal-actions"]}>
            <button
              type="button"
              className={styles["modal-done"]}
              onClick={() => setAboutOpen(false)}
            >
              Done
            </button>
          </footer>
        </div>
      </div>,
      document.body,
    );

  const footer =
    showFooter && hasFooterContent ? (
      <div
        className={
          styles["meta-footer"] +
          " " +
          styles[compact ? "compact" : "full"]
        }
      >
        <div className={styles["meta-bar"]}>
          {showAbout ? (
            <button
              type="button"
              className={styles["about-toggle"]}
              aria-haspopup="dialog"
              aria-expanded={aboutOpen}
              onClick={() => setAboutOpen(true)}
            >
              About
            </button>
          ) : (
            <span className={styles["meta-bar-spacer"]} aria-hidden="true" />
          )}

          {tagsPreview.length > 0 ? (
            <ul className={styles["tag-list"]} aria-label="Mood and style">
              {tagsPreview.map((tag) => {
                const active = isTagSelected(tag);
                return (
                  <li key={tag}>
                    <button
                      type="button"
                      className={
                        styles["tag-chip"] +
                        " " +
                        styles["tag-chip-button"] +
                        (active ? " " + styles["tag-chip-active"] : "")
                      }
                      aria-pressed={active}
                      title={
                        active
                          ? `Remove “${tag}” catalog filter`
                          : `Filter catalog by “${tag}”`
                      }
                      onClick={() => handleTagClick(tag)}
                    >
                      {tag}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className={styles["meta-bar-spacer"]} aria-hidden="true" />
          )}
        </div>
        {modal}
      </div>
    ) : null;

  // Modal can still open from footer; if only header zone, no modal chrome
  if (zone === "header") return header || null;
  if (zone === "footer") return footer || null;

  return (
    <div className={styles["meta-root"]}>
      {header}
      {footer}
    </div>
  );
};

export default CatalogItemMeta;
